import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Trophy, BarChart3, TrendingUp, Clock, Star, Users, DollarSign } from 'lucide-react';

export default function AnalyticsPage() {
  const { api } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/freelancer-leaderboard'),
      api.get('/projects'),
    ]).then(([l, p]) => {
      setLeaderboard(l.data);
      setProjects(p.data);
      if (p.data.length > 0) setSelectedProject(p.data[0]);
    }).catch(console.error);
  }, [api]);

  useEffect(() => {
    if (!selectedProject) return;
    Promise.all([
      api.get(`/analytics/project-burndown/${selectedProject.id}`),
      api.get(`/satisfaction/${selectedProject.id}`),
    ]).then(([b, s]) => { setBurndown(b.data); setSatisfaction(s.data); }).catch(() => {});
  }, [selectedProject, api]);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Performance Analytics</h1>
          <p className="text-xs text-zinc-500 mt-1">Freelancer leaderboard, project burndown, and client satisfaction</p>
        </div>
        <select data-testid="analytics-project-selector" value={selectedProject?.id || ''} onChange={e => setSelectedProject(projects.find(p => p.id === e.target.value))} className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 focus:outline-none">
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Project Burndown */}
      {burndown && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Total Tasks</p>
            <p className="text-xl font-heading font-bold text-white">{burndown.total_tasks}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Completed</p>
            <p className="text-xl font-heading font-bold text-emerald-400">{burndown.completed}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Blocked</p>
            <p className="text-xl font-heading font-bold text-red-400">{burndown.blocked}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Est. Days Left</p>
            <p className="text-xl font-heading font-bold text-amber-400">{burndown.est_days_to_complete}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase mb-1">Budget Left</p>
            <p className="text-xl font-heading font-bold text-blue-400 font-mono">{(burndown.budget_remaining / 100000).toFixed(1)}L</p>
          </div>
        </div>
      )}

      {/* Client Satisfaction */}
      {satisfaction && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Client Satisfaction</h3>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-lg font-heading font-bold text-amber-400">{satisfaction.average_score}/10</span>
              <span className="text-[10px] text-zinc-500">({satisfaction.total_reviews} reviews)</span>
            </div>
          </div>
          <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${satisfaction.average_score * 10}%` }} />
          </div>
        </div>
      )}

      {/* Freelancer Leaderboard */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-400" />
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Freelancer Leaderboard</h3>
        </div>
        <table className="w-full" data-testid="leaderboard-table">
          <thead>
            <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
              <th className="text-left px-4 py-2.5 font-medium">Rank</th>
              <th className="text-left px-4 py-2.5 font-medium">Freelancer</th>
              <th className="text-left px-4 py-2.5 font-medium">Skills</th>
              <th className="text-left px-4 py-2.5 font-medium">Score</th>
              <th className="text-left px-4 py-2.5 font-medium">On-Time</th>
              <th className="text-left px-4 py-2.5 font-medium">Avg Revisions</th>
              <th className="text-left px-4 py-2.5 font-medium">Tasks</th>
              <th className="text-left px-4 py-2.5 font-medium">Earnings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/30">
            {leaderboard.map((f, i) => (
              <tr key={f.id} className="hover:bg-zinc-800/20 transition-colors" data-testid={`leaderboard-${f.id}`}>
                <td className="px-4 py-2.5">
                  <span className={`w-6 h-6 inline-flex items-center justify-center rounded-sm text-[10px] font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-zinc-400/20 text-zinc-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-zinc-800 text-zinc-500'}`}>
                    #{i + 1}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <p className="text-xs text-zinc-200">{f.name}</p>
                  <p className="text-[10px] font-mono text-zinc-600">{f.email}</p>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 flex-wrap">{f.skills?.slice(0, 2).map(s => <span key={s} className="text-[8px] px-1 py-0.5 bg-zinc-800 text-zinc-500 rounded">{s}</span>)}</div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" /><span className="text-xs font-mono text-zinc-300">{f.score}</span></div>
                </td>
                <td className="px-4 py-2.5 text-xs font-mono text-emerald-400">{f.on_time_rate}%</td>
                <td className="px-4 py-2.5 text-xs font-mono text-zinc-400">{f.avg_revisions}</td>
                <td className="px-4 py-2.5 text-xs font-mono text-zinc-400">{f.completed}/{f.total_tasks}</td>
                <td className="px-4 py-2.5 text-xs font-mono text-emerald-400">{f.earnings?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
